/**
 * Merge a live teacher-evaluation websocket payload into the usecase's
 * last_teacher_evaluation_status.
 *
 * Mid-run ticks omit counters they didn't change, so the same distillation
 * event keeps the previous failed/warning/details. A *new* distillation
 * event must not inherit them — otherwise a re-eval shows the prior run's
 * "12 failed" next to the new run's "4/12 completed".
 */
export function mergeTeacherEvaluationStatus(existing = {}, incoming = {}) {
    const existingId = existing.distillation_event_id;
    const incomingId = incoming.distillation_event_id;
    const isNewRun = Boolean(
        existingId && incomingId && String(existingId) !== String(incomingId)
    );

    if (isNewRun) {
        return { ...incoming };
    }

    return {
        ...existing,
        ...incoming,
        completed: incoming.completed == null ? existing.completed : incoming.completed,
        total: incoming.total == null ? existing.total : incoming.total,
        failed: incoming.failed == null ? existing.failed : incoming.failed,
        warning: incoming.is_running
            ? incoming.warning == null
                ? existing.warning
                : incoming.warning
            : incoming.warning,
        eta_seconds: incoming.is_running
            ? incoming.eta_seconds == null
                ? existing.eta_seconds
                : incoming.eta_seconds
            : incoming.eta_seconds,
        quota_link: incoming.is_running
            ? incoming.quota_link == null
                ? existing.quota_link
                : incoming.quota_link
            : incoming.quota_link,
        azure_mode: incoming.is_running
            ? incoming.azure_mode == null
                ? existing.azure_mode
                : incoming.azure_mode
            : incoming.azure_mode,
        failure_details:
            incoming.failure_details == null
                ? existing.failure_details
                : incoming.failure_details,
    };
}

/**
 * Live usecase-level teacher status belongs to the most recently started run.
 * Ignore it when viewing a different distillation event so a new re-eval
 * cannot show the previous run's failed counts.
 */
export function teacherStatusForDisplayedRun(status, displayedEventId) {
    if (!status || !displayedEventId || !status.distillation_event_id) {
        return status || null;
    }
    return String(status.distillation_event_id) === String(displayedEventId) ? status : null;
}
